// Cloudflare Turnstile in "execute" mode: the widget stays invisible and only asks for a click when Cloudflare
// is unsure, in which case the slot App shows (under the address field, or a floating panel elsewhere) holds the
// checkbox until the person completes it. The widget moves to whichever slot the latest check was given.
interface TurnstileApi {
  render(container: HTMLElement, options: Record<string, unknown>): string;
  execute(widget: string): void;
  reset(widget: string): void;
  remove(widget: string): void;
}
declare global { interface Window { turnstile?: TurnstileApi } }

let script: Promise<TurnstileApi> | undefined;
function load(): Promise<TurnstileApi> {
  script ??= new Promise((resolve, reject) => {
    const tag = document.createElement('script');
    tag.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    tag.async = true;
    tag.onload = () => window.turnstile ? resolve(window.turnstile) : reject(new Error('missing api'));
    tag.onerror = () => { script = undefined; tag.remove(); reject(new Error('blocked')); };
    document.head.append(tag);
  });
  return script;
}

let widget: string | undefined;
let host: HTMLElement | undefined;
let pending: { resolve: (token: string) => void; reject: (error: Error) => void } | undefined;
const settle = (token?: string, error?: Error) => { const p = pending; pending = undefined; if (p) token ? p.resolve(token) : p.reject(error ?? new Error('failed')); };

export class HumanCheckCancelled extends Error {}
/** Abandons a check that is waiting for the person, for example when they close the panel. */
export function cancelHumanCheck(): void { const waiting = pending; settle(undefined, new HumanCheckCancelled('cancelled')); if (waiting) clear(); }

// Turnstile throws when it resets or removes a widget whose container React has already unmounted, which happens
// once a scan leaves the home page. That error must not escape into navigation, so a stale widget is forgotten.
function clear(): void {
  if (!widget) return;
  try { if (host?.isConnected) { window.turnstile?.reset(widget); return; } window.turnstile?.remove(widget); } catch { /* already gone */ }
  widget = undefined;
}

/** Resolves with a single-use token, or rejects with a message fit to show readers. */
export async function humanToken(siteKey: string, slot: HTMLElement, onInteractive: (open: boolean) => void): Promise<string> {
  let api: TurnstileApi;
  try { api = await load(); } catch { throw new Error('The human check could not load. If an ad or script blocker is on, allow challenges.cloudflare.com and try again.'); }
  settle(undefined, new HumanCheckCancelled('superseded'));
  if (widget && (host !== slot || !slot.isConnected)) { try { api.remove(widget); } catch { /* already gone */ } widget = undefined; }
  host = slot;
  if (!widget) widget = api.render(slot, {
    sitekey: siteKey, action: 'scan', execution: 'execute', appearance: 'interaction-only', theme: 'auto', 'refresh-expired': 'manual',
    callback: (token: string) => { onInteractive(false); settle(token); },
    'error-callback': () => { onInteractive(false); settle(undefined, new Error('We could not confirm that you are a person. Please try again.')); return true; },
    'expired-callback': () => { onInteractive(false); settle(undefined, new Error('The human check expired. Please try again.')); },
    'timeout-callback': () => { onInteractive(false); settle(undefined, new Error('The human check timed out. Please try again.')); },
    'before-interactive-callback': () => onInteractive(true),
    'after-interactive-callback': () => onInteractive(false),
  });
  else api.reset(widget);
  const token = new Promise<string>((resolve, reject) => { pending = { resolve, reject }; });
  api.execute(widget);
  try { return await token; } finally { clear(); }
}
